const DIGITAL_BOOK_CANVAS = {
    width: 800,
    height: 1120,
    spread: true,
    bleed: 0,
    safeArea: 48,
};
/** A4 portrait at the same design density as the digital book — not print DPI. */
const A4_DESIGN_CANVAS = {
    width: 800,
    height: Math.round((800 * 297) / 210),
};
const SCREEN_16_9_CANVAS = {
    width: 1280,
    height: 720,
    spread: false,
    bleed: 0,
    safeArea: 64,
};
export const MENU_FORMAT_CATALOG = {
    digital_book: {
        id: 'digital_book',
        family: 'book',
        flow: 'book',
        output: 'book',
        label: 'Digital book',
        hint: 'Cover, contents, category pages, spreads and back cover.',
        physical: { kind: 'design', label: 'Portrait book' },
        canvas: DIGITAL_BOOK_CANVAS,
        export: {
            kinds: ['pdf', 'html'],
            defaultKind: 'pdf',
            output: 'book',
            print: { profile: 'digital' },
        },
    },
    flyer: {
        id: 'flyer',
        family: 'print',
        flow: 'flat',
        output: 'page',
        label: 'Flyer',
        hint: 'A4 bifold panel. Facing pages show the outer and inner spread.',
        physical: {
            kind: 'print',
            paper: 'A4',
            widthMm: 210,
            heightMm: 297,
            label: 'A4 bifold panel · 210 × 297 mm',
        },
        canvas: {
            width: A4_DESIGN_CANVAS.width,
            height: A4_DESIGN_CANVAS.height,
            spread: true,
            bleed: 24,
            safeArea: 48,
        },
        export: {
            kinds: ['pdf', 'png', 'jpeg', 'html'],
            defaultKind: 'pdf',
            output: 'page',
            print: { profile: 'print', foldPanels: 2 },
        },
    },
    single_page: {
        id: 'single_page',
        family: 'print',
        flow: 'flat',
        output: 'page',
        label: 'Print sheet',
        hint: 'One or more A4 sheets without table of contents or spotlights.',
        physical: {
            kind: 'print',
            paper: 'A4',
            widthMm: 210,
            heightMm: 297,
            label: 'A4 · 210 × 297 mm',
        },
        canvas: {
            width: A4_DESIGN_CANVAS.width,
            height: A4_DESIGN_CANVAS.height,
            spread: false,
            bleed: 12,
            safeArea: 48,
        },
        export: {
            kinds: ['pdf', 'png', 'jpeg', 'html'],
            defaultKind: 'pdf',
            output: 'page',
            print: { profile: 'print' },
        },
    },
    interior_sheet: {
        id: 'interior_sheet',
        family: 'print',
        flow: 'flat',
        output: 'page',
        label: 'Interior sheet',
        hint: 'A4 interior print with extra bleed. Not a guest flip-book.',
        physical: {
            kind: 'print',
            paper: 'A4',
            widthMm: 210,
            heightMm: 297,
            label: 'A4 interior · 210 × 297 mm',
        },
        canvas: {
            width: A4_DESIGN_CANVAS.width,
            height: A4_DESIGN_CANVAS.height,
            spread: false,
            bleed: 24,
            safeArea: 56,
        },
        export: {
            kinds: ['pdf', 'png', 'jpeg', 'html'],
            defaultKind: 'pdf',
            output: 'page',
            print: { profile: 'print' },
        },
    },
    pocket_menu: {
        id: 'pocket_menu',
        family: 'print',
        flow: 'flat',
        output: 'page',
        label: 'Pocket menu',
        hint: 'Small A5 sheet. Keep copy short so type stays readable.',
        physical: {
            kind: 'print',
            paper: 'A5',
            widthMm: 148,
            heightMm: 210,
            label: 'A5 · 148 × 210 mm',
        },
        canvas: {
            width: A4_DESIGN_CANVAS.width,
            height: A4_DESIGN_CANVAS.height,
            spread: false,
            bleed: 16,
            safeArea: 48,
        },
        export: {
            kinds: ['pdf', 'png', 'jpeg', 'html'],
            defaultKind: 'pdf',
            output: 'page',
            print: { profile: 'print' },
        },
    },
    table_tent: {
        id: 'table_tent',
        family: 'print',
        flow: 'table-tent',
        output: 'page',
        label: 'Table tent',
        hint: 'Front panel plus one packed back panel. No contents or spotlights.',
        physical: {
            kind: 'print',
            paper: 'Tent',
            widthMm: 102,
            heightMm: 152,
            label: 'Tent · 100 × 150 mm',
        },
        canvas: { width: 800, height: 1200, spread: false, bleed: 0, safeArea: 48 },
        export: {
            kinds: ['pdf', 'png', 'jpeg', 'html'],
            defaultKind: 'pdf',
            output: 'page',
            print: { profile: 'print' },
        },
    },
    wall_board: {
        id: 'wall_board',
        family: 'screen',
        flow: 'flat',
        output: 'page',
        label: 'Wall banner',
        hint: 'Static Full HD landscape board. Keep capacity low for distance reading.',
        physical: {
            kind: 'screen',
            widthPx: 1920,
            heightPx: 1080,
            label: 'Full HD · 1920 × 1080',
        },
        canvas: { ...SCREEN_16_9_CANVAS },
        export: {
            kinds: ['png', 'jpeg', 'pdf'],
            defaultKind: 'png',
            output: 'page',
        },
    },
    tv_slide: {
        id: 'tv_slide',
        family: 'screen',
        flow: 'scene',
        output: 'scene',
        label: 'TV slide',
        hint: 'Full HD scene for a display. Large safe area; treat as a screen, not a book.',
        physical: {
            kind: 'screen',
            widthPx: 1920,
            heightPx: 1080,
            label: 'Full HD · 1920 × 1080',
        },
        canvas: { ...SCREEN_16_9_CANVAS, safeArea: 72 },
        export: {
            kinds: ['png', 'jpeg', 'pdf'],
            defaultKind: 'png',
            output: 'scene',
        },
    },
    social_post: {
        id: 'social_post',
        family: 'screen',
        flow: 'scene',
        output: 'scene',
        label: 'Social post',
        hint: 'Portrait 4:5 scene. Keep type large; pagination stays flat.',
        physical: {
            kind: 'screen',
            widthPx: 1080,
            heightPx: 1350,
            label: '4:5 · 1080 × 1350',
        },
        canvas: { width: 800, height: 1000, spread: false, bleed: 0, safeArea: 56 },
        export: {
            kinds: ['png', 'jpeg', 'pdf'],
            defaultKind: 'png',
            output: 'scene',
        },
    },
};
export const DEFAULT_CANVAS = MENU_FORMAT_CATALOG.digital_book.canvas;
/**
 * How much larger this artboard is than the reference digital-book page.
 *
 * Tokens and raw lengths are authored against that page. Print-DPI canvases
 * (A4 @ 300dpi) and Full HD screens would otherwise keep 16px type on a 2480px
 * leaf — it looks empty after the preview scales the page down to fit.
 */
export function canvasDensityScale(canvas) {
    const reference = Math.sqrt(DEFAULT_CANVAS.width * DEFAULT_CANVAS.height);
    const actual = Math.sqrt(Math.max(1, canvas.width) * Math.max(1, canvas.height));
    const scale = actual / reference;
    if (scale < 0.35)
        return 0.35;
    if (scale > 8)
        return 8;
    return scale;
}
export const CANVAS_PRESETS = {
    digital_book: MENU_FORMAT_CATALOG.digital_book.canvas,
    single_page: MENU_FORMAT_CATALOG.single_page.canvas,
    flyer: MENU_FORMAT_CATALOG.flyer.canvas,
    table_tent: MENU_FORMAT_CATALOG.table_tent.canvas,
    wall_board: MENU_FORMAT_CATALOG.wall_board.canvas,
    tv_slide: MENU_FORMAT_CATALOG.tv_slide.canvas,
    interior_sheet: MENU_FORMAT_CATALOG.interior_sheet.canvas,
    pocket_menu: MENU_FORMAT_CATALOG.pocket_menu.canvas,
    social_post: MENU_FORMAT_CATALOG.social_post.canvas,
};
export const FORMAT_FAMILIES = ['book', 'print', 'screen'];
export function isMenuFormat(value) {
    return typeof value === 'string' && Object.hasOwn(MENU_FORMAT_CATALOG, value);
}
export function formatCatalogEntry(format) {
    return MENU_FORMAT_CATALOG[format];
}
export function lookupFormatCatalog(format) {
    return isMenuFormat(format) ? MENU_FORMAT_CATALOG[format] : undefined;
}
export function formatFamily(format) {
    return MENU_FORMAT_CATALOG[format].family;
}
export function formatOutput(format) {
    return MENU_FORMAT_CATALOG[format].output;
}
export function formatFlow(format) {
    return MENU_FORMAT_CATALOG[format].flow;
}
export function physicalLabel(format) {
    return MENU_FORMAT_CATALOG[format].physical.label;
}
export function exportSpecForFormat(format) {
    const spec = MENU_FORMAT_CATALOG[format].export;
    return {
        kinds: [...spec.kinds],
        defaultKind: spec.defaultKind,
        output: spec.output,
        print: spec.print ? { ...spec.print } : undefined,
    };
}
/** True when category frames should iterate the whole menu, not a book page. */
export function isFlatMenuFormat(format) {
    return MENU_FORMAT_CATALOG[format].flow !== 'book';
}
export function isBookMenuFormat(format) {
    return MENU_FORMAT_CATALOG[format].family === 'book';
}
export function formatsInFamily(family) {
    return Object.keys(MENU_FORMAT_CATALOG).filter((id) => MENU_FORMAT_CATALOG[id].family === family);
}
export function exportKindsForFormat(format) {
    return [...MENU_FORMAT_CATALOG[format].export.kinds];
}
//# sourceMappingURL=format-catalog.js.map